import { Link } from 'react-router-dom';
import useText from '@/hooks/useText';
import { slugifyTrackingValue, trackEvent } from '@/utils/analytics';

const trackFooterRoute = (href: string, label: string) =>
  trackEvent({
    name: 'navigation_click',
    buttonId: `navigation_route_${slugifyTrackingValue(href)}`,
    label,
    page: 'global',
    section: 'footer',
    feature: 'navigation',
    eventCategory: 'navigation',
  });

function Footer() {
  const logo = useText('home.footer.logo');
  const desc = useText('home.footer.desc');
  const headingCompany = useText('home.footer.heading_company');
  const about = useText('home.footer.about');
  const docs = useText('navigation.docs');
  const press = useText('home.footer.press');
  const headingLegal = useText('home.footer.heading_legal');
  const legalNotice = useText('home.footer.legal_notice');
  const legalLicenses = useText('home.footer.legal_licenses');
  const privacy = useText('home.footer.privacy');
  const headingResources = useText('home.footer.heading_resources');
  const headingHelp = useText('home.footer.heading_help');
  const documentation = useText('home.footer.documentation');
  const support = useText('home.footer.support');
  const cite = useText('home.footer.cite');
  const copyright = useText('home.footer.copyright');
  const plans = useText('home.cta.btn_view_plans');
  const aboutOurCompany = useText('home.footer.about_our_company');
  const caseStudies = useText('home.footer.case_studies');
  const partners = useText('home.footer.partners');
  const linkedinUrl = useText('home.footer.linkedin_url');
  const publications = useText('publications.title');

  return (
    <footer className="home-footer">
      <div className="home-footer-container">
        <div className="home-footer-grid">
          <div className="home-footer-brand">
            <Link to="/" className="home-footer-logo" onClick={() => trackFooterRoute('/', logo)}>
              {logo}
            </Link>
            <p className="home-footer-desc">{desc}</p>
            <div className="home-footer-social">
              <a
                href={linkedinUrl}
                target="_blank"
                rel="noreferrer"
                aria-label="LinkedIn"
                onClick={() => trackFooterRoute(linkedinUrl, 'LinkedIn')}
              >
                <img
                  src="../../static/images/linkedin-in.svg"
                  alt="LinkedIn"
                  className="home-footer-social-icon"
                />
              </a>
              <a
                href="https://www.youtube.com/channel/UCGazBaBHYyx4-fIW8TZXYbg"
                target="_blank"
                rel="noreferrer"
                aria-label="YouTube"
                onClick={() =>
                  trackFooterRoute('https://www.youtube.com/channel/UCGazBaBHYyx4-fIW8TZXYbg', 'YouTube')
                }
              >
                <img
                  src="../../static/images/youtube.svg"
                  alt="YouTube"
                  className="home-footer-social-icon"
                />
              </a>
            </div>
          </div>
          <div className="home-footer-col">
            <h4 className="home-footer-heading">{headingCompany}</h4>
            <ul className="home-footer-links">
              <li>
                <Link
                  to="https://medbioinformatics.com/"
                  onClick={() => trackFooterRoute('https://medbioinformatics.com/', aboutOurCompany)}
                >
                  {aboutOurCompany}
                </Link>
              </li>
              <li>
                <a
                  href="https://blog.disgenet.com/"
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => trackFooterRoute('https://blog.disgenet.com/', press)}
                >
                  {press}
                </a>
              </li>
              <li>
                <a
                  href="https://casestudies.disgenet.com"
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => trackFooterRoute('https://casestudies.disgenet.com', caseStudies)}
                >
                  {caseStudies}
                </a>
              </li>
              <li>
                <Link to="/partners" onClick={() => trackFooterRoute('/partners', partners)}>
                  {partners}
                </Link>
              </li>
            </ul>
          </div>
          <div className="home-footer-col">
            <h4 className="home-footer-heading">{headingLegal}</h4>
            <ul className="home-footer-links">
              <li>
                <Link to="/legal-notice" onClick={() => trackFooterRoute('/legal-notice', legalNotice)}>
                  {legalNotice}
                </Link>
              </li>
              <li>
                <Link to="/Legal" onClick={() => trackFooterRoute('/Legal', legalLicenses)}>
                  {legalLicenses}
                </Link>
              </li>
              <li>
                <Link to="/Privacy-policy" onClick={() => trackFooterRoute('/Privacy-policy', privacy)}>
                  {privacy}
                </Link>
              </li>
            </ul>
          </div>
          <div className="home-footer-col">
            <h4 className="home-footer-heading">{headingResources}</h4>
            <ul className="home-footer-links">
              <li>
                <a href="/about" rel="noopener noreferrer" onClick={() => trackFooterRoute('/about', about)}>
                  {about}
                </a>
              </li>
              <li>
                <a href="/docs" rel="noopener noreferrer" onClick={() => trackFooterRoute('/docs', docs)}>
                  {docs}
                </a>
              </li>
              <li>
                <Link
                  to="/docs?section=cite_us"
                  onClick={() => trackFooterRoute('/docs?section=cite_us', cite)}
                >
                  {cite}
                </Link>
              </li>
              <li>
                <a href="/plans" rel="noreferrer" onClick={() => trackFooterRoute('/plans', plans)}>
                  {plans}
                </a>
              </li>
              <li>
                <Link to="/publications" onClick={() => trackFooterRoute('/publications', publications)}>
                  {publications}
                </Link>
              </li>
            </ul>
          </div>
          <div className="home-footer-col">
            <h4 className="home-footer-heading">{headingHelp}</h4>
            <ul className="home-footer-links">
              <li>
                <a
                  href="https://support.disgenet.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackFooterRoute('https://support.disgenet.com/', documentation)}
                >
                  {documentation}
                </a>
              </li>
              <li>
                <Link to="/Support" onClick={() => trackFooterRoute('/Support', support)}>
                  {support}
                </Link>
              </li>
            </ul>
          </div>
        </div>
        <div className="home-footer-copy">{copyright}</div>
      </div>
    </footer>
  );
}

export default Footer;
